/**
 * Multiple Games — Brand foundation.
 * SINGLE SOURCE OF TRUTH for all UI text, legal entity, theme.
 *
 * Rule: never hard-code the brand name, tagline, or contact info anywhere
 * else. Pull from BRAND. Touch this one file when copy/legal changes.
 */
export const BRAND = {
    name: "Multiple Games",
    shortName: "MG",
    legalEntity: "Multiple Games Pvt. Ltd.",

    // Domains / contact
    domain: "multiple.games",
    url: "https://multiple.games",
    supportEmail: "support@multiple.games",
    legalEmail: "legal@multiple.games",
    pressEmail: "press@multiple.games",

    // Single contact line — used for both call AND text.
    // Display format (US-style): "+1 350 206 0466"
    // E.164 format (for tel: / wa.me / SMS APIs): "+13502060466"
    contactPhone: "+1 350 206 0466",
    contactPhoneE164: "+13502060466",
    contactPhoneTel: "+13502060466",  // tel: href
    contactWhatsappLink: "https://wa.me/13502060466",

    // Marketing
    tagline: "Free to play. Real rewards.",
    description:
        "America's premium social casino — fishing, slots, poker and more. " +
        "Free to play, with real prizes through our sweepstakes model.",

    // Theme tokens (kept in sync with tailwind / chat.css)
    primary: "#f59e0b", // amber-500
    primaryDark: "#b45309", // amber-700
    accent: "#8b5cf6", // violet-500
    background: "#0a0a0f",

    // Compliance — US social casino
    ageGate: 18,
    governingLaw: "Delaware, USA",
    arbitrationVenue: "Wilmington, Delaware (AAA)",
    restrictedStates: [
        "WA", "ID", "NV", "MI", "MT", "AL", "AR", "KS", "KY", "LA", "MS", "MO",
    ],
    responsibleGamingUrl: "https://www.ncpgambling.org",
    ncpgHotline: "1-800-522-4700",

    // Versioning — bump to force re-acceptance of T&Cs
    tosVersion: "2026-05-01",

    // Copyright
    copyrightYear: 2026,

    // Logo placeholder — swap for real SVG path when ready.
    logoUrl: null, // null → render the MG monogram
};

export const COPYRIGHT_LINE = `© ${BRAND.copyrightYear} ${BRAND.legalEntity}. All rights reserved.`;

export const RESPONSIBLE_PLAY_LINE =
    `${BRAND.ageGate}+ · Play responsibly · Problem gambling? Call ${BRAND.ncpgHotline}`;

export default BRAND;


// ============================================================
// Phase 2 — vendor-aware brand context (additive; existing
// `BRAND` import keeps working for callers that haven't migrated).
//
// Render contract: every consumer of `useBrand()` MUST get a complete
// brand on first paint — no `null`, no `undefined`. We seed with the
// platform brand synchronously, then resolve the real vendor brand
// from the network in the background. If the fetch fails or times
// out, the consumer keeps showing the platform brand. No state ever
// crashes the tree.
// ============================================================
import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

// Canonical shape we hand to consumers. Matches the response of
// /api/public/brand/* on the backend (see brand_resolver.py). Every
// field is guaranteed present.
const PLATFORM_BRAND_RESOLVED = {
    display_name: BRAND.name,
    short_name: BRAND.shortName,
    tagline: BRAND.tagline,
    description: BRAND.description,
    logo_url: BRAND.logoUrl,
    logo_dark_url: null,
    favicon_url: null,
    og_image_url: null,
    primary_color: BRAND.primary,
    primary_dark: BRAND.primaryDark,
    accent_color: BRAND.accent,
    background_color: BRAND.background,
    text_color: "#ffffff",
    support_email: BRAND.supportEmail,
    support_phone: BRAND.contactPhone,
    support_whatsapp: BRAND.contactWhatsappLink,
    legal_entity: BRAND.legalEntity,
    copyright_line: COPYRIGHT_LINE,
    footer_powered_by: false, // platform doesn't say "powered by itself"
    seo_title_suffix: BRAND.name,
    vendor_id: null,
    vendor_slug: null,
};

const BrandContext = createContext({
    brand: PLATFORM_BRAND_RESOLVED,
    isVendorScoped: false,
    isLoading: false,
    // No-op defaults so consumers can call these unconditionally even
    // outside of a BrandProvider (e.g. during unit tests). The real
    // provider replaces them with the network-backed versions.
    setBrandFromInvite: () => {},
    setBrandFromSlug: () => {},
});

export function useBrand() {
    return useContext(BrandContext);
}


// ----- helpers --------------------------------------------------

// Reserved /clubs/* sub-paths that look like a vendor slug but are
// actually page names. Without this blacklist the BrandProvider fires
// a stray /api/public/brand/by-slug/signup call which gets a 200 +
// platform-brand fallback, and that fallback races the legitimate
// /api/public/brand/by-invite/<code> resolve and overwrites it — the
// player ends up seeing neutral MG branding on a club invite link.
const RESERVED_CLUBS_SUBPATHS = new Set([
    "signup",
    "login",
    "signin",
    "forgot",
    "magic",
    "set-password",
    "verify",
    "verification",
    "logout",
    "new",
    "create",
]);

function deriveSlugFromPath(pathname) {
    // Recognise the two URL shapes that today carry a vendor slug:
    //   /clubs/<slug>/...   (multi-club picker UI on the neutral domain)
    //   /v/<slug>/...       (legacy single-vendor portal)
    if (!pathname) return null;
    const m = pathname.match(/^\/(?:clubs|v)\/([a-z0-9-]+)/i);
    if (!m) return null;
    const candidate = m[1].toLowerCase();
    if (RESERVED_CLUBS_SUBPATHS.has(candidate)) return null;
    return candidate;
}

function isVendorHostname(host) {
    // The neutral platform domain itself is NOT vendor-scoped — only
    // subdomains / non-matching custom domains count. Keep this list
    // small and explicit; the backend's resolver still has final say.
    if (!host) return false;
    const lower = host.toLowerCase().split(":")[0];
    return (
        lower !== "multiple.games" &&
        lower !== "www.multiple.games" &&
        lower !== "zone.multiple.games" &&
        lower !== "localhost" &&
        !lower.startsWith("127.") &&
        !lower.startsWith("192.168.") &&
        !lower.endsWith(".local")
    );
}

const HOST_BRAND_CACHE_PREFIX = "mgPublicHostBrand:v1:";

function currentVendorHost() {
    if (typeof window === "undefined") return "";
    const host = window.location.hostname.toLowerCase().split(":")[0];
    return isVendorHostname(host) ? host : "";
}

function cachedBrandForCurrentHost() {
    const host = currentVendorHost();
    if (!host) return PLATFORM_BRAND_RESOLVED;
    try {
        const raw = window.localStorage.getItem(`${HOST_BRAND_CACHE_PREFIX}${host}`);
        const cached = raw ? JSON.parse(raw) : null;
        if (cached?.vendor_slug) {
            return { ...PLATFORM_BRAND_RESOLVED, ...cached };
        }
    } catch {
        /* storage is best-effort */
    }
    return PLATFORM_BRAND_RESOLVED;
}

function cacheBrandForCurrentHost(brand) {
    const host = currentVendorHost();
    if (!host || !brand?.vendor_slug) return;
    try {
        window.localStorage.setItem(
            `${HOST_BRAND_CACHE_PREFIX}${host}`,
            JSON.stringify(brand),
        );
    } catch {
        /* storage is best-effort */
    }
}

function safeFetchJson(url, timeoutMs = 4000) {
    // Wrap fetch in a 4-second hard timeout so a slow / down backend
    // never delays the React tree or holds connections.
    return new Promise((resolve) => {
        let done = false;
        const finish = (v) => {
            if (done) return;
            done = true;
            resolve(v);
        };
        const t = setTimeout(() => finish(null), timeoutMs);
        try {
            fetch(url, { credentials: "omit" })
                .then((r) => (r && r.ok ? r.json() : null))
                .then((d) => {
                    clearTimeout(t);
                    finish(d);
                })
                .catch(() => {
                    clearTimeout(t);
                    finish(null);
                });
        } catch {
            clearTimeout(t);
            finish(null);
        }
    });
}

function applyBrandToDocument(brand) {
    // Apply the resolved brand to the document so unstyled / outside-React
    // consumers (head tags, favicon, browser chrome) update too. All ops
    // are best-effort; any failure is silently ignored.
    try {
        const root = document.documentElement;
        if (!root) return;
        const set = (name, val) => {
            if (val) root.style.setProperty(name, val);
        };
        set("--brand-primary", brand.primary_color);
        set("--brand-primary-dark", brand.primary_dark);
        set("--brand-accent", brand.accent_color);
        set("--brand-bg", brand.background_color);
        set("--brand-text", brand.text_color);

        // Page title — append the vendor suffix when we have one.
        if (brand.seo_title_suffix && brand.vendor_slug) {
            // Only override <title> when we're vendor-scoped; otherwise
            // leave whatever the route set (we don't want to clobber
            // page-specific titles on the neutral domain).
            try {
                if (
                    document.title &&
                    !document.title.includes(brand.seo_title_suffix)
                ) {
                    document.title = `${document.title} · ${brand.seo_title_suffix}`;
                }
            } catch {/* ignore */}
        }

        // Favicon swap (only when vendor provided one).
        if (brand.favicon_url) {
            try {
                let link = document.querySelector("link[rel~='icon']");
                if (!link) {
                    link = document.createElement("link");
                    link.rel = "icon";
                    document.head.appendChild(link);
                }
                link.href = brand.favicon_url.startsWith("http")
                    ? brand.favicon_url
                    : brand.favicon_url;
            } catch {/* ignore */}
        }

        // Mobile browser chrome (the address-bar tint on iOS Safari /
        // Android Chrome). Falls back to primary when no explicit value.
        if (brand.primary_color) {
            try {
                let meta = document.querySelector("meta[name='theme-color']");
                if (!meta) {
                    meta = document.createElement("meta");
                    meta.name = "theme-color";
                    document.head.appendChild(meta);
                }
                meta.content = brand.primary_color;
            } catch {/* ignore */}
        }
    } catch {/* ignore — never throw from here */}
}


// ----- provider -------------------------------------------------

export function BrandProvider({ children }) {
    // A custom/vendor host is a permanent tenant boundary. Seed it from the
    // last public brand response so React remounts and offline starts never
    // flash the generic Multiple Games shell while /by-host resolves.
    const [brand, setBrand] = useState(cachedBrandForCurrentHost);
    const [isLoading, setIsLoading] = useState(() => !!currentVendorHost());
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        // Best-effort resolution. Three paths, first hit wins:
        //   1. URL path slug — /clubs/<slug>/* or /v/<slug>/*
        //   2. Hostname subdomain / custom domain (backend resolver)
        //   3. Platform brand (default)
        let cancelled = false;
        const resolve = async () => {
            setIsLoading(true);
            try {
                const slug = deriveSlugFromPath(window.location.pathname);
                let payload = null;
                if (slug) {
                    payload = await safeFetchJson(
                        `/api/public/brand/by-slug/${encodeURIComponent(slug)}`,
                    );
                } else if (isVendorHostname(window.location.hostname)) {
                    payload = await safeFetchJson(`/api/public/brand/by-host`);
                }
                if (cancelled || !mountedRef.current) return;
                if (payload && payload.brand) {
                    const next = { ...PLATFORM_BRAND_RESOLVED, ...payload.brand };
                    setBrand(next);
                    cacheBrandForCurrentHost(next);
                    applyBrandToDocument(next);
                } else {
                    // A vendor hostname must fail closed. Keep its cached or
                    // current tenant brand instead of exposing platform UI.
                    applyBrandToDocument(
                        currentVendorHost() ? brand : PLATFORM_BRAND_RESOLVED,
                    );
                }
            } catch {
                // never throw from resolve
            } finally {
                if (!cancelled && mountedRef.current) {
                    setIsLoading(false);
                }
            }
        };
        resolve();
        return () => {
            cancelled = true;
        };
        // Re-resolve when the URL changes (SPA navigation between clubs).
        // We listen to popstate; consumers triggering pushState should
        // also dispatch a custom event if they want immediate re-resolve,
        // but most navigations between /clubs/<a> ↔ /clubs/<b> happen via
        // anchor click + popstate.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const onNav = () => {
            // Lightweight re-derive: same logic as initial mount but
            // we only trigger a re-fetch if the slug actually changed.
            const slug = deriveSlugFromPath(window.location.pathname);
            const prev = brand.vendor_slug;
            if (slug && slug !== prev) {
                safeFetchJson(
                    `/api/public/brand/by-slug/${encodeURIComponent(slug)}`,
                ).then((payload) => {
                    if (!mountedRef.current) return;
                    if (payload && payload.brand) {
                        const next = {
                            ...PLATFORM_BRAND_RESOLVED,
                            ...payload.brand,
                        };
                        setBrand(next);
                        applyBrandToDocument(next);
                    }
                });
            } else if (!slug && currentVendorHost()) {
                // Clean tenant URLs (/chat, /credits, /rewards, /) carry
                // their identity in the hostname. History navigation must
                // never reset those routes to the generic platform brand.
                safeFetchJson(`/api/public/brand/by-host`).then((payload) => {
                    if (!mountedRef.current) return;
                    if (payload && payload.brand) {
                        const next = {
                            ...PLATFORM_BRAND_RESOLVED,
                            ...payload.brand,
                        };
                        setBrand(next);
                        cacheBrandForCurrentHost(next);
                        applyBrandToDocument(next);
                    }
                });
            } else if (!slug && prev) {
                // Only the neutral platform host may leave vendor scope.
                setBrand(PLATFORM_BRAND_RESOLVED);
                applyBrandToDocument(PLATFORM_BRAND_RESOLVED);
            }
        };
        window.addEventListener("popstate", onNav);
        return () => window.removeEventListener("popstate", onNav);
    }, [brand.vendor_slug]);

    // ----- Imperative mutators ------------------------------------
    //
    // The URL-based auto-resolution above covers /clubs/<slug> and
    // hostname-based vendor scoping. Some entry points (notably the
    // invite-link signup at /clubs/signup?code=ABCDEF or ?join=slug)
    // need to flip the brand based on a query param the path-matcher
    // never sees. These two methods let any consumer trigger a
    // re-resolve without round-tripping the URL.
    //
    // Both are no-ops if the lookup fails (graceful fallback to the
    // current brand — never throws, never blanks the page).

    const applyFetched = (payload) => {
        if (!mountedRef.current) return;
        if (payload && payload.brand) {
            const next = { ...PLATFORM_BRAND_RESOLVED, ...payload.brand };
            setBrand(next);
            cacheBrandForCurrentHost(next);
            applyBrandToDocument(next);
        }
    };

    const setBrandFromInvite = (code) => {
        if (!code) return;
        safeFetchJson(
            `/api/public/brand/by-invite/${encodeURIComponent(code)}`,
        ).then(applyFetched);
    };

    const setBrandFromSlug = (slug) => {
        if (!slug) return;
        safeFetchJson(
            `/api/public/brand/by-slug/${encodeURIComponent(slug)}`,
        ).then(applyFetched);
    };

    const value = useMemo(
        () => ({
            brand,
            // Host scope is authoritative even before or during network
            // resolution. Generic platform branches are unreachable on a
            // vendor/custom domain.
            isVendorScoped: !!brand.vendor_slug || !!currentVendorHost(),
            isLoading,
            setBrandFromInvite,
            setBrandFromSlug,
        }),
        // setBrandFromInvite/Slug close over stable refs (setBrand from
        // useState, mountedRef) so they're identity-stable; no need to
        // depend on them.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [brand, isLoading],
    );

    return React.createElement(BrandContext.Provider, { value }, children);
}
